import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PhoneNumberUtil, PhoneNumberFormat, PhoneNumberType } from 'google-libphonenumber';

/**
 * Helper pour la gestion des numéros de téléphone
 * 
 * Exemple d'utilisation :
 * 
 * // Validation d'un numéro
 * const validator = createPhoneNumberValidator(() => 'FR');
 * const result = validator(control);
 * 
 * // Formatage d'un numéro
 * const formatted = PhoneHelper.formatPhoneNumber('0123456789', 'FR');
 * // Retourne: +33 1 23 45 67 89
 * 
 * // Vérification de validité
 * const isValid = PhoneHelper.isPhoneNumberValid('0123456789', 'FR');
 * 
 * // Type de numéro
 * const type = PhoneHelper.getPhoneNumberType('0123456789', 'FR');
 * // Retourne: 'mobile', 'fixed-line', etc.
 * 
 * // Conversion préfixe -> code pays
 * const countryCode = getCountryCodeFromPhonePrefix('+33');
 * // Retourne: 'fr'
 */
export interface Country {
  name: {
    common: string;
    official: string;
  };
  cca2: string;
  idd: {
    root: string;
    suffixes: string[];
  };
  flags: {
    png: string;
    svg: string;
  };
}

export interface ProcessedCountry {
  name: string;
  code: string;
  dialCode: string;
  flag: string;
}

/**
 * Shape for prefilling `app-phone-field` from a backend phone string.
 * Invalid / empty backend values should map to `null` so the UI stays blank.
 */
export interface PrefillPhoneValue {
  /** National-format display string for the inner input (no dial code). */
  phone: string;
  dialCode: string;
  countryCode: string;
  /** E.164 for storing raw `phone` on the parent form / API if needed. */
  e164: string;
}

const PREFILL_PARSE_REGIONS = ['FR', 'TN', 'BE', 'CH', 'DE', 'IT', 'ES', 'GB', 'LU', 'NL', 'PL'] as const;

/**
 * Parses backend `phone` once; returns structured prefill or `null` if missing/invalid.
 * Tries several default regions so local numbers without `+` can still validate.
 */
export function normalizePrefillForPhoneField(raw: string | null | undefined): PrefillPhoneValue | null {
  if (raw == null) {
    return null;
  }
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return null;
  }

  const phoneUtil = PhoneNumberUtil.getInstance();
  for (const region of PREFILL_PARSE_REGIONS) {
    try {
      const parsed = phoneUtil.parse(trimmed, region);
      if (!phoneUtil.isValidNumber(parsed)) {
        continue;
      }
      const e164 = phoneUtil.format(parsed, PhoneNumberFormat.E164);
      const national = phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL);
      const cc = parsed.getCountryCode();
      const dialCode = `+${cc}`;
      const countryCode = phoneUtil.getRegionCodeForNumber(parsed) || String(region);
      return {
        phone: national,
        dialCode,
        countryCode,
        e164,
      };
    } catch {
      // try next region
    }
  }
  return null;
}

/**
 * Returns true when user must complete phone before continuing app usage.
 */
export function needsPhoneCompletion(userClient: any): boolean {
  if (!userClient) {
    return false;
  }
  return normalizePrefillForPhoneField(userClient.phone) === null;
}

// Fonction pour convertir le préfixe téléphonique en code pays (Europe uniquement)
export function getCountryCodeFromPhonePrefix(phonePrefix: string): string {
  const countryMap: { [key: string]: string } = {
    '+33': 'fr', // France
    '+49': 'de', // Allemagne
    '+39': 'it', // Italie
    '+34': 'es', // Espagne
    '+44': 'gb', // Royaume-Uni
    '+31': 'nl', // Pays-Bas
    '+32': 'be', // Belgique
    '+41': 'ch', // Suisse
    '+43': 'at', // Autriche
    '+46': 'se', // Suède
    '+47': 'no', // Norvège
    '+45': 'dk', // Danemark
    '+358': 'fi', // Finlande
    '+48': 'pl', // Pologne
    '+420': 'cz', // République tchèque
    '+36': 'hu', // Hongrie
    '+40': 'ro', // Roumanie
    '+421': 'sk', // Slovaquie
    '+386': 'si', // Slovénie
    '+371': 'lv', // Lettonie
    '+372': 'ee', // Estonie
    '+370': 'lt', // Lituanie
    '+351': 'pt', // Portugal
    '+352': 'lu', // Luxembourg
    '+353': 'ie', // Irlande
    '+354': 'is', // Islande
    '+355': 'al', // Albanie
    '+356': 'mt', // Malte
    '+357': 'cy', // Chypre
    '+359': 'bg', // Bulgarie
    '+373': 'md', // Moldavie
    '+374': 'am', // Arménie
    '+375': 'by', // Biélorussie
    '+376': 'ad', // Andorre
    '+377': 'mc', // Monaco
    '+378': 'sm', // Saint-Marin
    '+379': 'va', // Vatican
    '+380': 'ua', // Ukraine
    '+381': 'rs', // Serbie
    '+382': 'me', // Monténégro
    '+383': 'xk', // Kosovo
    '+385': 'hr', // Croatie
    '+387': 'ba', // Bosnie-Herzégovine
    '+389': 'mk', // Macédoine du Nord
    '+30': 'gr', // Grèce
    '+90': 'tr', // Turquie (partiellement européen)
    '+7': 'ru', // Russie (partiellement européen)
  };

  return countryMap[phonePrefix] || 'fr'; // Retourne 'fr' par défaut si pas trouvé
}

// Validateur de numéro de téléphone utilisant google-libphonenumber
export function createPhoneNumberValidator(getCountryCode: () => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const phoneNumber = control.value ? control.value.toString().trim() : "";
    const countryCode = getCountryCode();

    if (!phoneNumber || !countryCode) {
      return null; // Laisse le validateur required gérer les champs vides
    }

    try {
      const phoneUtil = PhoneNumberUtil.getInstance();
      
      // Parse le numéro avec le code pays
      const parsedNumber = phoneUtil.parse(phoneNumber, countryCode);
      
      // Vérifie si le numéro est valide pour ce pays
      if (phoneUtil.isValidNumber(parsedNumber)) {
        return null; // Numéro valide
      } else {
        return { phoneNumberInvalid: true };
      }
    } catch (error) {
      // Erreur de parsing (format incorrect)
      return { phoneNumberFormat: true };
    }
  };
}

// Validateur de longueur de numéro de téléphone (ancien validateur pour compatibilité)
export function getPhoneNumberLengthValidator(getCountryCode: () => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    let phoneNumber = control.value ? control.value.toString().replace(/\s+/g, "") : "";
    const countryCode = (getCountryCode() || '').toUpperCase();

    if (phoneNumber.startsWith('0')) {
      phoneNumber = phoneNumber.substring(1);
    }

    const expectedLengths: { [key: string]: number } = {
      'FR': 9, // France
      'BE': 9, // Belgique
      'DE': 10, // Allemagne
      'IT': 10, // Italie
      'ES': 9, // Espagne
      'PT': 9, // Portugal
      'NL': 9, // Pays-Bas
      'LU': 9, // Luxembourg
      'CH': 9, // Suisse
      'AT': 10, // Autriche
      'DK': 8, // Danemark
      'SE': 9, // Suède
      'NO': 8, // Norvège
      'FI': 9, // Finlande
      'IS': 7, // Islande
      'IE': 9, // Irlande
      'GB': 10, // Royaume-Uni
      'GR': 10, // Grèce
      'CY': 8, // Chypre
      'MT': 8, // Malte
      'SI': 8, // Slovénie
      'HR': 9, // Croatie
      'HU': 9, // Hongrie
      'CZ': 9, // République tchèque
      'SK': 9, // Slovaquie
      'PL': 9, // Pologne
      'EE': 7, // Estonie
      'LV': 8, // Lettonie
      'LT': 8, // Lituanie
      'RO': 9, // Roumanie
      'BG': 8, // Bulgarie
      'MD': 8, // Moldavie
    };

    const expectedLength = expectedLengths[countryCode];
    if (expectedLength && phoneNumber.length > 0 && phoneNumber.length !== expectedLength) {
      return { phoneNumberLength: expectedLength };
    }
    return null;
  };
}

// Classe utilitaire pour la gestion des numéros de téléphone
export class PhoneHelper {
  private static phoneUtil = PhoneNumberUtil.getInstance();

  // Formater un numéro de téléphone
  static formatPhoneNumber(phoneNumber: string, countryCode: string): string {
    try {
      const parsedNumber = this.phoneUtil.parse(phoneNumber, countryCode);
      
      if (this.phoneUtil.isValidNumber(parsedNumber)) {
        return this.phoneUtil.format(parsedNumber, PhoneNumberFormat.INTERNATIONAL);
      }
    } catch (error) {
      console.warn('Error formatting phone number:', error);
    }
    return phoneNumber;
  }

  // Vérifier si un numéro est valide
  static isPhoneNumberValid(phoneNumber: string, countryCode: string): boolean {
    try {
      const parsedNumber = this.phoneUtil.parse(phoneNumber, countryCode);
      return this.phoneUtil.isValidNumber(parsedNumber);
    } catch (error) {
      return false;
    }
  }

  // Obtenir le type de numéro (mobile, fixed-line, etc.)
  static getPhoneNumberType(phoneNumber: string, countryCode: string): string {
    try {
      const parsedNumber = this.phoneUtil.parse(phoneNumber, countryCode);
      
      if (this.phoneUtil.isValidNumber(parsedNumber)) {
        const numberType = this.phoneUtil.getNumberType(parsedNumber);
        switch (numberType) {
          case PhoneNumberType.MOBILE:
            return 'mobile';
          case PhoneNumberType.FIXED_LINE:
            return 'fixed-line';
          case PhoneNumberType.FIXED_LINE_OR_MOBILE:
            return 'fixed-line-or-mobile';
          default:
            return 'unknown';
        }
      }
    } catch (error) {
      console.warn('Error getting phone number type:', error);
    }
    return 'unknown';
  }

  // Traiter les données de pays depuis l'API
  static processCountriesData(data: Country[]): ProcessedCountry[] {
    return data.map(country => ({
      name: country.name.common,
      code: country.cca2,
      dialCode: country.idd.root + (country.idd.suffixes?.[0] || ''),
      flag: country.flags.png
    })).sort((a, b) => a.name.localeCompare(b.name));
  }

  // Liste de fallback des pays européens
  static getFallbackCountries(): ProcessedCountry[] {
    return [
      { name: 'France', code: 'FR', dialCode: '+33', flag: '🇫🇷' },
      { name: 'Germany', code: 'DE', dialCode: '+49', flag: '🇩🇪' },
      { name: 'Italy', code: 'IT', dialCode: '+39', flag: '🇮🇹' },
      { name: 'Spain', code: 'ES', dialCode: '+34', flag: '🇪🇸' },
      { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧' },
      { name: 'Netherlands', code: 'NL', dialCode: '+31', flag: '🇳🇱' },
      { name: 'Belgium', code: 'BE', dialCode: '+32', flag: '🇧🇪' },
      { name: 'Switzerland', code: 'CH', dialCode: '+41', flag: '🇨🇭' },
      { name: 'Austria', code: 'AT', dialCode: '+43', flag: '🇦🇹' },
      { name: 'Sweden', code: 'SE', dialCode: '+46', flag: '🇸🇪' },
      { name: 'Norway', code: 'NO', dialCode: '+47', flag: '🇳🇴' },
      { name: 'Denmark', code: 'DK', dialCode: '+45', flag: '🇩🇰' },
      { name: 'Finland', code: 'FI', dialCode: '+358', flag: '🇫🇮' },
      { name: 'Poland', code: 'PL', dialCode: '+48', flag: '🇵🇱' },
      { name: 'Czech Republic', code: 'CZ', dialCode: '+420', flag: '🇨🇿' },
      { name: 'Hungary', code: 'HU', dialCode: '+36', flag: '🇭🇺' },
      { name: 'Romania', code: 'RO', dialCode: '+40', flag: '🇷🇴' },
      { name: 'Slovakia', code: 'SK', dialCode: '+421', flag: '🇸🇰' },
      { name: 'Slovenia', code: 'SI', dialCode: '+386', flag: '🇸🇮' },
      { name: 'Latvia', code: 'LV', dialCode: '+371', flag: '🇱🇻' },
      { name: 'Estonia', code: 'EE', dialCode: '+372', flag: '🇪🇪' },
      { name: 'Lithuania', code: 'LT', dialCode: '+370', flag: '🇱🇹' },
      { name: 'Portugal', code: 'PT', dialCode: '+351', flag: '🇵🇹' },
      { name: 'Luxembourg', code: 'LU', dialCode: '+352', flag: '🇱🇺' },
      { name: 'Ireland', code: 'IE', dialCode: '+353', flag: '🇮🇪' },
      { name: 'Iceland', code: 'IS', dialCode: '+354', flag: '🇮🇸' },
      { name: 'Albania', code: 'AL', dialCode: '+355', flag: '🇦🇱' },
      { name: 'Malta', code: 'MT', dialCode: '+356', flag: '🇲🇹' },
      { name: 'Cyprus', code: 'CY', dialCode: '+357', flag: '🇨🇾' },
      { name: 'Bulgaria', code: 'BG', dialCode: '+359', flag: '🇧🇬' },
      { name: 'Moldova', code: 'MD', dialCode: '+373', flag: '🇲🇩' },
      { name: 'Armenia', code: 'AM', dialCode: '+374', flag: '🇦🇲' },
      { name: 'Belarus', code: 'BY', dialCode: '+375', flag: '🇧🇾' },
      { name: 'Andorra', code: 'AD', dialCode: '+376', flag: '🇦🇩' },
      { name: 'Monaco', code: 'MC', dialCode: '+377', flag: '🇲🇨' },
      { name: 'San Marino', code: 'SM', dialCode: '+378', flag: '🇸🇲' },
      { name: 'Vatican City', code: 'VA', dialCode: '+379', flag: '🇻🇦' },
      { name: 'Ukraine', code: 'UA', dialCode: '+380', flag: '🇺🇦' },
      { name: 'Serbia', code: 'RS', dialCode: '+381', flag: '🇷🇸' },
      { name: 'Montenegro', code: 'ME', dialCode: '+382', flag: '🇲🇪' },
      { name: 'Kosovo', code: 'XK', dialCode: '+383', flag: '🇽🇰' },
      { name: 'Croatia', code: 'HR', dialCode: '+385', flag: '🇭🇷' },
      { name: 'Bosnia and Herzegovina', code: 'BA', dialCode: '+387', flag: '🇧🇦' },
      { name: 'North Macedonia', code: 'MK', dialCode: '+389', flag: '🇲🇰' },
      { name: 'Greece', code: 'GR', dialCode: '+30', flag: '🇬🇷' },
      { name: 'Turkey', code: 'TR', dialCode: '+90', flag: '🇹🇷' },
      { name: 'Russia', code: 'RU', dialCode: '+7', flag: '🇷🇺' }
    ];
  }
} 